import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Card,
    Col,
    Row,
    Button,
    Typography,
    Tag,
    message,
    Layout,
    Select,
    Avatar,
    Statistic,
    Spin,
    Empty,
    Tooltip,
    Modal,
    Table
} from 'antd';
import {
    ReloadOutlined,
    BarChartOutlined,
    UserSwitchOutlined,
    UserOutlined,
    AppstoreOutlined,
    DragOutlined,
    UndoOutlined,
    BookOutlined
} from '@ant-design/icons';

import { AuthContext } from "../../config/AuthContext";
import { useNavigate } from "react-router-dom";
import AppSider from "./components/AppSider";
import { GetAlumneId } from "../../dao/GetAlumneId";
import { DefaultAulumneInfo } from "../../model/AlumneInfo";
import { GetAulaRaConfig } from "../../dao/GetAulaRaConfig";
import { GetBbddActivitats } from "../../dao/bbdd/GetBbddActivitats";
import { GetInteraccionsHistory } from "../../dao/GetInteraccionsHistory";
import ActivitatAlumnesBbddModal from "./problemes/content/ActivitatAlumnesBbddModal";
import { GetClassroomLayout, PostClassroomLayout } from "../../dao/jutge/ClassroomLayoutDao";
import Login from "../Login";
import { GetUsuarisIdBbddNotes } from '../../dao/bbdd/GetUsuarisIdBbddNotes';
import { GetUsuarisIdAulaIdBbddNotes } from '../../dao/bbdd/GetUsuarisIdAulaIdBbddNotes';

const { Title, Text } = Typography;
const { Content } = Layout;
const { Option, OptGroup } = Select;

// --- Interfaces ---

interface StudentNote {
    id_usuari: number;
    id_practica: number;
    nota: number;
}

interface NoteRA {
    id_usuari: number;
    nom_ra: string;
    nota: number;
}

interface StudentData {
    id: number;
    ide?: number | string;
    nom: string;
    cognoms: string;
    avatar?: string;
    averageGrade?: number;
    completedTasks?: number;
    totalTasks?: number;
    raGrades?: { nom: string; nota: number | null; isCapped?: boolean }[];
}

interface ClassroomData {
    alumnes: StudentData[];
    practiques: any[];
    notes: StudentNote[];
    notes_ra: NoteRA[];
    ras_aula: any[];
}

// Helper: Normalize avatar image format
function getAvatarSrc(img: string | undefined | null) {
    if (!img || img === "undefined" || img === "null") return undefined;
    if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
    if (img.startsWith("base64:")) img = img.split(":").pop()!;
    if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
    if (img.startsWith("iVB")) return `data:image/png;base64,${img}`;
    return `data:image/png;base64,${img}`;
}

const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const getGradeColor = (grade: number) => {
    if (grade >= 9) return '#52c41a';
    if (grade >= 7) return '#1890ff';
    if (grade >= 5) return '#faad14';
    return '#f5222d';
};

// Stacked Bar Chart for RAs
const RaStackedBarChart: React.FC<{ students: StudentData[]; ras: string[] }> = ({ students, ras }) => {
    const ranges = [
        { label: 'Excelente (9-10)', min: 9, max: 11, color: '#52c41a', textColor: '#237804' },
        { label: 'Notable (7-8.9)', min: 7, max: 9, color: '#1890ff', textColor: '#0050b3' },
        { label: 'Bien (6-6.9)', min: 6, max: 7, color: '#faad14', textColor: '#ad6800' },
        { label: 'Aprobado (5-5.9)', min: 5, max: 6, color: '#fa8c16', textColor: '#873800' },
        { label: 'Suspenso (0-4.9)', min: 0, max: 5, color: '#f5222d', textColor: '#a8071a' },
    ];

    if (ras.length === 0) return <Empty description="Sin datos de RA" />;

    return (
        <div style={{ padding: '10px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 16, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                {ras.map(raName => {
                    const counts = ranges.map(r => ({ ...r, count: 0 }));
                    let validGradesCount = 0;

                    students.forEach(s => {
                        const ra = s.raGrades?.find(r => r.nom === raName);
                        const val = ra && ra.nota !== null ? ra.nota : null;

                        if (val !== null) {
                            const range = counts.find(r => val >= r.min && val < r.max);
                            if (range) {
                                range.count++;
                                validGradesCount++;
                            }
                        }
                    });

                    const total = validGradesCount || 1;

                    return (
                        <div key={raName} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div style={{ width: '100%', maxWidth: 40, height: '100%', background: '#f5f5f5', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                {validGradesCount === 0 ? (
                                    <div style={{ height: '4px', background: '#d9d9d9', width: '100%' }} />
                                ) : (
                                    counts.map((range, idx) => {
                                        const percent = (range.count / total) * 100;
                                        if (percent === 0) return null;
                                        return (
                                            <Tooltip key={idx} title={`${range.label}: ${range.count} alumnos (${percent.toFixed(0)}%)`}>
                                                <div style={{
                                                    height: `${percent}%`,
                                                    background: range.color,
                                                    width: '100%',
                                                    transition: 'height 0.3s'
                                                }} />
                                            </Tooltip>
                                        );
                                    })
                                )}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 'bold' }}>{raName}</div>
                        </div>
                    );
                })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                {ranges.map(r => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, backgroundColor: r.color, borderRadius: 2 }} />
                        <Text style={{ fontSize: 11, color: '#666' }}>{r.label.split(' ')[0]}</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Student Detail Modal ---
const StudentDetailModal: React.FC<{
    visible: boolean;
    onClose: () => void;
    student: StudentData | null;
    classroomData: ClassroomData | null;
    raConfig: any[];
}> = ({ visible, onClose, student, classroomData, raConfig }) => {
    if (!student || !classroomData) return null;

    const allRAs = Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];
    const studentRANotes = new Map((student.raGrades || []).map(r => [r.nom, { nota: r.nota, isCapped: r.isCapped }]));

    const raData = allRAs.map((ra: any) => {
        const raInfo = studentRANotes.get(ra.nom);
        return {
            nom_ra: ra.nom,
            nota: raInfo ? raInfo.nota : null,
            isCapped: raInfo ? raInfo.isCapped : false
        };
    }).sort((a: any, b: any) => a.nom_ra.localeCompare(b.nom_ra));

    const safeNotes = Array.isArray(classroomData.notes) ? classroomData.notes : [];
    const safePractiques = Array.isArray(classroomData.practiques) ? classroomData.practiques : [];

    const mapNotas = new Map(safeNotes.filter(n => n.id_usuari === student.id)
        .map(n => [`${n.id_practica}`, n.nota]));

    const raColumns = [
        { title: 'Resultados de Aprendizaje (RA)', dataIndex: 'nom_ra', key: 'nom_ra', render: (text: string) => <b>RA-{text}</b> },
        {
            title: 'Nota Global',
            dataIndex: 'nota',
            key: 'nota',
            align: 'right' as const,
            render: (val: number | null, record: any) => {
                if (val === null) return <Tag>N/P</Tag>;
                const color = val >= 5 ? 'green' : 'red';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        {record.isCapped && (
                            <Tooltip title="Nota limitada a 4 por no alcanzar el mínimo en un instrumento">
                                <span style={{ cursor: 'help', fontSize: 16 }}>⚠️</span>
                            </Tooltip>
                        )}
                        <Tag color={color} style={{ fontSize: 14, padding: '4px 10px' }}>{val.toFixed(2)}</Tag>
                    </div>
                );
            }
        }
    ];

    const renderExpandedRa = (record: any) => {
        const raPractices = safePractiques.filter(p =>
            p.ras && p.ras.some((r: any) => r.nom === record.nom_ra)
        );

        if (raPractices.length === 0) return <Empty description="No hay actividades asociadas" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

        const instrumentMap: Record<string, any[]> = {};
        raPractices.forEach(p => {
            const raObj = p.ras?.find((r: any) => r.nom === record.nom_ra);
            const inst = raObj?.instrument || p.inst || 'Sin Instrumento';
            if (!instrumentMap[inst]) instrumentMap[inst] = [];
            instrumentMap[inst].push(p);
        });

        const instrumentData = Object.keys(instrumentMap).map(inst => {
            const practices = instrumentMap[inst];
            const configItems = raConfig.filter((c: any) => c.nom_ra === record.nom_ra);
            const conf = configItems.find((c: any) => c.instrument === inst);
            const totalWeight = conf ? conf.pes : 0;

            const grades = practices.map(p => mapNotas.get(`${p.id}`)).filter(g => g !== null && g !== undefined) as number[];
            const avgGrade = grades.length > 0 ? (grades.reduce((a: number, b: number) => a + b, 0) / grades.length) / 10 : null;

            return {
                key: inst,
                instrument: inst,
                weight: totalWeight,
                avgGrade: avgGrade,
                practices: practices
            };
        });

        const nestedColumns = [
            { title: 'Instrumento Evaluativo', dataIndex: 'instrument', key: 'instrument', render: (text: string) => <b>{text}</b> },
            {
                title: 'Peso en RA',
                dataIndex: 'weight',
                key: 'weight',
                align: 'center' as const,
                render: (val: number) => <Tag color={val > 0 ? "blue" : "default"}>{val}%</Tag>
            },
            {
                title: 'Nota Media Instrumento',
                dataIndex: 'avgGrade',
                key: 'avgGrade',
                align: 'right' as const,
                render: (val: number | null) => {
                    if (val === null) return <Tag>N/P</Tag>;
                    return <Tag color={val >= 5 ? 'green' : 'red'}>{val.toFixed(2)}</Tag>;
                }
            }
        ];

        return (
            <div style={{ margin: '8px 0', background: '#fafafa', padding: 12, borderRadius: 6 }}>
                <Table
                    columns={nestedColumns}
                    dataSource={instrumentData}
                    pagination={false}
                    size="small"
                />
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar src={getAvatarSrc(student.avatar)} size="large">{student.nom[0]}</Avatar>
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{student.nom} {student.cognoms}</div>
                        <div style={{ fontSize: 12, fontWeight: 'normal', color: '#666' }}>Detalle de Calificaciones de Base de Datos (Sobre 10)</div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={[<Button key="close" onClick={onClose}>Cerrar</Button>]}
            width={900}
        >
            <Table
                columns={raColumns}
                dataSource={raData}
                rowKey="nom_ra"
                pagination={false}
                expandable={{
                    expandedRowRender: renderExpandedRa,
                    expandRowByClick: true
                }}
            />
        </Modal>
    );
};

// Helper to parse GetBbddActivitats response safely
function parseBbddActivitatsList(raw: any): any[] {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const p = JSON.parse(raw);
            return Array.isArray(p) ? p : [];
        } catch {
            return [];
        }
    }
    if (raw && raw.json) {
        try {
            const p = typeof raw.json === 'string' ? JSON.parse(raw.json) : raw.json;
            return Array.isArray(p) ? p : [];
        } catch {
            return [];
        }
    }
    return [];
}

// --- Dashboard Component ---
const Dashboard = () => {
    const navigate = useNavigate();
    const { userId, setLoggedIn, setUserId, setUser, setAlumneInfo, setUserPermisos, setBreadCrumb, user, alumne, isLoggedIn } = useContext(AuthContext);

    const [aulas, setAulas] = useState<{ id: number; nom: string }[]>([]);
    const [selectedAula, setSelectedAula] = useState<number | null>(null);
    const [classroomData, setClassroomData] = useState<ClassroomData | null>(null);
    const [loadingData, setLoadingData] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [raConfig, setRaConfig] = useState<any[]>([]);
    const [allActivitats, setAllActivitats] = useState<any[]>([]);
    const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);

    // Layout mode: 'aula' (free layout) | 'graella' (grid)
    const [viewMode, setViewMode] = useState<'aula' | 'graella'>('aula');
    const [cardPositions, setCardPositions] = useState<Record<string, { x: number; y: number }>>({});
    const [, setSavingLayout] = useState(false);

    // Student Dashboard state
    const isStudent = user && user.tipus !== "0" && user.tipus !== "1";
    const [studentActivities, setStudentActivities] = useState<any[]>([]);
    const [activeChats, setActiveChats] = useState(0);
    const [closedChats, setClosedChats] = useState(0);
    const [participation, setParticipation] = useState(0);

    useEffect(() => {
        if (!isLoggedIn) return;

        // Student Exam Mode Redirection
        if (user?.tipus === "2") {
            const userAulas = alumne?.aules || [];
            const isExamMode = (userAulas || []).some((a: any) =>
                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
            );
            if (isExamMode) {
                navigate('/database/examens');
                return;
            }
        }

        setBreadCrumb([{ nom: 'Home', nav: '/home' }, { nom: 'bbdd', nav: '/database' }], true);

        if (!isStudent) {
            fetchTeacherDashboard();
        } else {
            fetchStudentData();
        }
    }, [isLoggedIn, isStudent, userId]);

    const fetchTeacherDashboard = async () => {
        try {
            const [notesData, actsRaw] = await Promise.all([
                GetUsuarisIdBbddNotes(userId + ""),
                GetBbddActivitats(userId + "")
            ]);

            const parsedActs = parseBbddActivitatsList(actsRaw);
            setAllActivitats(parsedActs);

            const uniqueAulasMap = new Map<number, string>();

            // Classrooms from BBDD activities
            parsedActs.forEach((act: any) => {
                if (act && act.id_aula) {
                    uniqueAulasMap.set(Number(act.id_aula), act.aula || `Aula ${act.id_aula}`);
                }
            });

            // Classrooms from BBDD student notes
            const list: any[] = Array.isArray(notesData) ? notesData : [];
            list.flatMap(student => student.practiques || []).forEach((p: any) => {
                if (p && p.id_aula) {
                    uniqueAulasMap.set(Number(p.id_aula), p.aula || `Aula ${p.id_aula}`);
                }
            });

            const aulasList = Array.from(uniqueAulasMap.entries()).map(([id, nom]) => ({ id, nom }));
            setAulas(aulasList);
            if (aulasList.length > 0 && (!selectedAula || !uniqueAulasMap.has(Number(selectedAula)))) {
                setSelectedAula(aulasList[0].id);
            } else if (aulasList.length === 0) {
                setSelectedAula(null);
            }
        } catch (error) {
            console.error(error);
            message.error("Error cargando aulas de Base de Datos");
        }
    };

    useEffect(() => {
        if (selectedAula) {
            setSelectedItemKey(null);
            fetchClassroomStats(selectedAula);
        } else {
            setClassroomData(null);
            setSelectedItemKey(null);
            setCardPositions({});
        }
    }, [selectedAula]);

    const fetchClassroomStats = async (aulaId: number) => {
        setLoadingData(true);
        try {
            const [res, config, layout] = await Promise.all([
                GetUsuarisIdAulaIdBbddNotes(userId + "", aulaId + ""),
                GetAulaRaConfig(aulaId + ""),
                GetClassroomLayout(aulaId)
            ]);
            setClassroomData(res);
            setRaConfig(config || []);
            if (layout && typeof layout === 'object' && Object.keys(layout).length > 0) {
                setCardPositions(layout);
            } else {
                setCardPositions({});
            }
        } catch (error) {
            console.error(error);
            message.error("Error cargando datos del aula");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSaveLayout = async (positionsToSave?: Record<string, { x: number; y: number }>) => {
        if (!selectedAula) return;
        const toSave = positionsToSave || cardPositions;
        setSavingLayout(true);
        try {
            const success = await PostClassroomLayout(selectedAula, toSave);
            if (success) {
                message.success("Disposición del aula guardada correctamente");
            } else {
                message.error("Error al guardar la disposición");
            }
        } catch (e) {
            console.error("Error guardando layout aula:", e);
            message.error("Error al guardar la disposición");
        } finally {
            setSavingLayout(false);
        }
    };

    const handleResetLayout = () => {
        setCardPositions({});
        if (selectedAula) {
            PostClassroomLayout(selectedAula, {});
            message.info("Disposición restablecida");
        }
    };

    const handleImpersonate = async (targetId: string) => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            message.loading("Impersonando usuario...");

            const response = await fetch(`${baseUrl}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId })
            });

            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;

                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }

                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }

                localStorage.setItem('authToken', token);

                setLoggedIn(true);
                setUserId(String(permisos[0].id));
                setUser(permisos[0]);
                setUserPermisos(permisos);
                setAlumneInfo(alu);

                message.success('Impersonando...');
                window.location.reload();
            } else {
                message.error('Error al impersonar');
            }
        } catch (e) {
            console.error(e);
            message.error('Error de conexión');
        }
    };

    const handleStudentClick = (student: StudentData) => {
        setSelectedStudent(student);
        setModalVisible(true);
    };

    // --- Computed Data ---
    const processedStudents = useMemo(() => {
        if (!classroomData || !Array.isArray(classroomData.alumnes)) return [];

        const safeNotes = Array.isArray(classroomData.notes) ? classroomData.notes : [];
        const safePractiques = Array.isArray(classroomData.practiques) ? classroomData.practiques : [];
        const safeRasAula = Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];
        const safeNotesRa = Array.isArray(classroomData.notes_ra) ? classroomData.notes_ra : [];
        const safeRaConfig = Array.isArray(raConfig) ? raConfig : [];

        const mapNotas = new Map(safeNotes.map((n: any) => [`${n.id_usuari}-${n.id_practica}`, n.nota]));

        return classroomData.alumnes.map(student => {
            const raGrades = safeRasAula.map((ra: any) => {
                const raName = ra.nom;
                const configItems = safeRaConfig.filter((c: any) => c.nom_ra === raName);

                let raTotal: number | null = 0;
                let calculated = false;
                let failedInstrument = false;

                if (configItems.length > 0) {
                    const totalWeight = configItems.filter(c => !c.isRecovery).reduce((sum, c) => sum + (c.pes || 0), 0);

                    if (totalWeight === 0) {
                        const dbVal = safeNotesRa.find(n => n.id_usuari === student.id && n.nom_ra === raName);
                        if (dbVal !== undefined) {
                            return { nom: raName, nota: dbVal.nota, isCapped: false };
                        } else {
                            const raPractices = safePractiques.filter((p: any) =>
                                p.ras?.some((r: any) => r.nom === raName)
                            );
                            let sum = 0;
                            let count = 0;
                            raPractices.forEach((p: any) => {
                                const note = mapNotas.get(`${student.id}-${p.id}`);
                                if (note !== null && note !== undefined) {
                                    sum += Number(note);
                                    count++;
                                }
                            });
                            const avg = count > 0 ? (sum / count) / 10 : null;
                            return { nom: raName, nota: avg, isCapped: false };
                        }
                    }

                    calculated = true;
                    const instGrades: Record<string, number | null> = {};

                    configItems.forEach((conf: any) => {
                        const inst = conf.instrument;
                        const practices = safePractiques.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === raName && r.instrument === inst)
                        );

                        let instAvg: number | null = null;
                        if (practices.length > 0) {
                            const grades = practices.map((p: any) => mapNotas.get(`${student.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                            if (grades.length > 0) {
                                instAvg = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                            }
                        }
                        instGrades[inst] = instAvg;
                    });

                    const modifiedGrades = { ...instGrades };
                    let finalGradeOverride: number | null = null;
                    let finalGradeCapped = false;

                    configItems.forEach((item: any) => {
                        if (!item.isRecovery) return;

                        let recGrade = instGrades[item.instrument];
                        if (recGrade === null || recGrade === undefined || recGrade <= 0) return;

                        if (recGrade > 10) recGrade /= 10;

                        if (item.recoveryType === 'final_grade') {
                            const rawCap = item.notaMaxima;
                            const cap = (rawCap !== null && rawCap !== undefined && rawCap !== '') ? parseFloat(rawCap) : null;
                            let computedGrade = recGrade;

                            if (cap !== null && !isNaN(cap) && recGrade > cap) {
                                computedGrade = cap;
                            }

                            if (finalGradeOverride !== null && finalGradeOverride >= 5 && computedGrade < finalGradeOverride) {
                                // Mantener la nota
                            } else {
                                finalGradeOverride = computedGrade;
                                finalGradeCapped = finalGradeOverride < (item.minim || 0);
                            }
                        } else {
                            const target = item.targetInstrument || 'Prueba Practica';
                            const currentGrade = modifiedGrades[target] ?? 0;

                            if (item.recoveryType === 'max') {
                                modifiedGrades[target] = Math.max(currentGrade, recGrade);
                            } else if (item.recoveryType === 'average') {
                                modifiedGrades[target] = (currentGrade + recGrade) / 2;
                            } else if (item.recoveryType === 'replace') {
                                modifiedGrades[target] = recGrade;
                            }
                        }
                    });

                    raTotal = 0;
                    configItems.forEach((conf: any) => {
                        if (conf.isRecovery) return;

                        const grade = modifiedGrades[conf.instrument];

                        if (grade !== null && grade !== undefined) {
                            const min = conf.minim || 0;
                            if (grade < min) {
                                failedInstrument = true;
                            }
                            raTotal! += (grade * ((conf.pes || 0) / 100));
                            calculated = true;
                        }
                    });

                    if (finalGradeOverride !== null && finalGradeOverride > raTotal!) {
                        raTotal = finalGradeOverride;
                        failedInstrument = finalGradeCapped;
                        calculated = true;
                    }

                    if (!calculated && finalGradeOverride === null) {
                        const raPractices = safePractiques.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === raName)
                        );
                        let hasAnySubmission = false;
                        for (const p of raPractices) {
                            const note = mapNotas.get(`${student.id}-${p.id}`);
                            if (note !== null && note !== undefined) {
                                hasAnySubmission = true;
                                break;
                            }
                        }
                        if (hasAnySubmission) {
                            raTotal = 0;
                            calculated = true;
                        }
                    }
                } else {
                    const dbVal = safeNotesRa.find(n => n.id_usuari === student.id && n.nom_ra === raName);
                    if (dbVal !== undefined) {
                        return { nom: raName, nota: dbVal.nota, isCapped: false };
                    } else {
                        const raPractices = safePractiques.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === raName)
                        );
                        let sum = 0;
                        let count = 0;
                        raPractices.forEach((p: any) => {
                            const note = mapNotas.get(`${student.id}-${p.id}`);
                            if (note !== null && note !== undefined) {
                                sum += Number(note);
                                count++;
                            }
                        });
                        const avg = count > 0 ? (sum / count) / 10 : null;
                        return { nom: raName, nota: avg, isCapped: false };
                    }
                }

                if (!calculated) {
                    return { nom: raName, nota: null, isCapped: false };
                }

                let finalScore = raTotal;
                let isCapped = false;

                if (failedInstrument && finalScore! > 4) {
                    finalScore = 4;
                    isCapped = true;
                }

                return {
                    nom: raName,
                    nota: finalScore,
                    isCapped
                };
            });

            // Calculate overall average
            const validGrades = raGrades.filter(r => r.nota !== null).map(r => r.nota as number);
            const avgGrade = validGrades.length > 0 ? validGrades.reduce((a, b) => a + b, 0) / validGrades.length : 0;

            let completed = 0;
            safePractiques.forEach((p: any) => {
                const note = mapNotas.get(`${student.id}-${p.id}`);
                if (note !== null && note !== undefined && Number(note) > 0) completed++;
            });

            const studentAvatar = getAvatarSrc(student.avatar);

            return {
                ...student,
                avatar: studentAvatar,
                averageGrade: avgGrade,
                completedTasks: completed,
                totalTasks: safePractiques.length,
                raGrades
            };
        });
    }, [classroomData, raConfig]);

    const globalStats = useMemo(() => {
        const totalStudents = processedStudents.length;
        if (totalStudents === 0) return { globalAvg: 0, raAverages: [] };

        const allRAs = classroomData && Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];
        const raAverages = allRAs.map((ra: any) => {
            let sum = 0;
            let count = 0;
            processedStudents.forEach(s => {
                const rGrade = s.raGrades?.find(r => r.nom === ra.nom);
                if (rGrade && rGrade.nota !== null) {
                    sum += rGrade.nota;
                    count++;
                }
            });
            return {
                nom: ra.nom,
                avg: count > 0 ? sum / count : 0
            };
        });

        const studentsWithGrades = processedStudents.filter(s => (s.averageGrade || 0) > 0);
        const globalAvg = studentsWithGrades.length > 0
            ? studentsWithGrades.reduce((acc, s) => acc + (s.averageGrade || 0), 0) / studentsWithGrades.length
            : 0;

        return {
            globalAvg,
            raAverages
        };
    }, [processedStudents, classroomData]);

    const fetchStudentData = async () => {
        setLoadingData(true);
        try {
            const userAulas = alumne?.aules || [];
            const isExamMode = (userAulas || []).some((a: any) =>
                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
            );

            const [actsRaw, interactions] = await Promise.all([
                GetBbddActivitats(userId + ""),
                GetInteraccionsHistory(userId)
            ]);

            const parsedActs = parseBbddActivitatsList(actsRaw);

            // Only show classrooms that have at least one activity in BBDD
            const aulesWithBbddMap = new Map<number, string>();
            parsedActs.forEach((act: any) => {
                if (act && act.id_aula) {
                    aulesWithBbddMap.set(Number(act.id_aula), act.aula || `Aula ${act.id_aula}`);
                }
            });

            const aList = Array.from(aulesWithBbddMap.entries()).map(([id, nom]) => ({ id, nom }));
            setAulas(aList);

            if (aList.length > 0) {
                const currentAulaId = (selectedAula && aulesWithBbddMap.has(Number(selectedAula)))
                    ? selectedAula
                    : aList[0].id;
                setSelectedAula(currentAulaId);
                await fetchClassroomStats(currentAulaId);
            } else {
                setSelectedAula(null);
            }

            if (isExamMode) {
                setStudentActivities([]);
            } else {
                setStudentActivities(
                    parsedActs
                        .filter((a: any) => a.estat !== 1)
                        .sort((a: any, b: any) => (b.ordre || 0) - (a.ordre || 0))
                );
            }

            if (interactions && Array.isArray(interactions)) {
                const active = interactions.filter((i: any) => i.estat === 0).length;
                const closed = interactions.filter((i: any) => i.estat !== 0).length;
                setActiveChats(active);
                setClosedChats(closed);
                setParticipation(interactions.length > 0 ? 100 : 0);
            }
        } catch (e) {
            console.error("Error loading student dashboard data", e);
        } finally {
            setLoadingData(false);
        }
    };

    if (!isLoggedIn) {
        return <Login />;
    }

    // --- STUDENT VIEW (user.tipus === "2") ---
    if (isStudent) {
        const currentStudent = processedStudents.find((s: any) => String(s.id) === String(userId));

        const renderItem = (p: any) => {
            const val = p.progres ?? p.nota;
            const isNP = val === null || val === undefined;
            const safeVal = typeof val === 'number' ? val : 0;
            const percent = safeVal;

            return (
                <Col span={12} key={p.id}>
                    <Card
                        size="small"
                        hoverable
                        onClick={() => {
                            const userAulas = alumne?.aules || [];
                            const isExamMode = (userAulas || []).some((a: any) =>
                                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
                            );
                            if (isExamMode) {
                                const isExam = Number(p.is_examen ?? p.isExamen) === 1;
                                if (!isExam || p.estat !== 3) return;
                            }
                            navigate(`/database/activitats/${p.id}`);
                        }}
                        style={{ cursor: 'pointer', height: '100%' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{p.nom}</Text>
                            {(p.estat === 2 || p.estat === 1) && <Tag color="red">Tancat</Tag>}
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                <span>Progrés: {!isNP ? Number(safeVal).toFixed(2) : ''}</span>
                                {isNP ? <Tag color="purple">N/P</Tag> : <span>{Number(safeVal).toFixed(2)}%</span>}
                            </div>
                            {!isNP && (
                                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min(percent, 100)}%`, background: getGradeColor(percent / 10), transition: 'width 0.3s' }} />
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            );
        };

        return (
            <Layout style={{ minHeight: '100vh' }}>
                <AppSider currentVal={0} setPantalla={() => { }} />
                <Content style={{ margin: '24px' }}>
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {aulas.length > 0 && (
                            <Select
                                value={selectedAula}
                                style={{ width: 250 }}
                                onChange={setSelectedAula}
                                loading={loadingData}
                            >
                                {aulas.map(a => <Option key={a.id} value={a.id}>{a.nom}</Option>)}
                            </Select>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={fetchStudentData}>Refrescar</Button>
                    </div>

                    {loadingData ? <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div> : (
                        <Row gutter={[24, 24]}>
                            {/* User Profile & Grades Card */}
                            <Col xs={24} md={8}>
                                <Card title="El meu perfil" bordered={false} style={{ height: '100%' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                                        <Avatar src={getAvatarSrc(currentStudent?.avatar || (alumne as any)?.alumne?.avatar)} size={100} style={{ marginBottom: 10 }}>
                                            {currentStudent?.nom?.[0] || 'U'}
                                        </Avatar>
                                        <Title level={4}>{currentStudent?.nom} {currentStudent?.cognoms}</Title>
                                        <Tag color="blue">Estudiant</Tag>
                                    </div>
                                    <Statistic
                                        title="Mitjana Global"
                                        value={currentStudent?.averageGrade || 0}
                                        precision={2}
                                        valueStyle={{ color: getGradeColor(currentStudent?.averageGrade || 0) }}
                                    />
                                    <div style={{ marginTop: 20 }}>
                                        <Text strong>Notes per RA:</Text>
                                        <div style={{ marginTop: 10 }}>
                                            {currentStudent?.raGrades?.map((ra: any) => (
                                                <div key={ra.nom} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                                    <Text>{ra.nom}</Text>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {ra.isCapped && (
                                                            <Tooltip title="Nota limitada a 4 por no alcanzar el mínimo en un instrumento">
                                                                <span style={{ cursor: 'help', fontSize: 12 }}>⚠️</span>
                                                            </Tooltip>
                                                        )}
                                                        <Tag color={ra.nota !== null ? getGradeColor(ra.nota) : "purple"}>
                                                            {ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} md={16}>
                                <Card title="Activitat i Xat" bordered={false} style={{ marginBottom: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Statistic title="Converses Actives" value={activeChats} prefix={<UserSwitchOutlined />} valueStyle={{ color: '#1890ff' }} />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic title="Converses Tancades" value={closedChats} />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic title="Participació (%)" value={participation} suffix="%" valueStyle={{ color: '#3f8600' }} />
                                            <div style={{ fontSize: 12, color: '#999' }}>Converses d'altres companys</div>
                                        </Col>
                                    </Row>
                                </Card>

                                <Card title="Activitats de BBDD" bordered={false}>
                                    {studentActivities.length > 0 ? (
                                        <Row gutter={[16, 16]}>
                                            {studentActivities.map(p => renderItem(p))}
                                        </Row>
                                    ) : <Empty description="Cap activitat disponible" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Content>
            </Layout>
        );
    }

    // --- TEACHER VIEW (user.tipus === "0" | "1") ---
    const selectedActivityObj = selectedItemKey && selectedItemKey.startsWith('act-')
        ? allActivitats.find((act: any) => String(act.id) === selectedItemKey.replace('act-', ''))
        : null;

    const aulaActivitatsCount = allActivitats.filter((act: any) => String(act.id_aula) === String(selectedAula)).length;

    return (
        <>
            <AppSider currentVal={0} setPantalla={() => { }} />
            <Content style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 64px)' }}>
                <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Title level={2} style={{ margin: 0, color: '#1f1f1f' }}>Dashboard de Base de Dades</Title>
                        <Text type="secondary">Monitorització del progrés i qualificacions de Base de Dades</Text>
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Select
                            style={{ width: 240 }}
                            placeholder="Selecciona un Aula"
                            value={selectedAula}
                            onChange={(val) => {
                                setSelectedAula(val);
                                setSelectedItemKey(null);
                            }}
                            size="large"
                        >
                            {aulas.map(a => <Option key={a.id} value={a.id}>{a.nom}</Option>)}
                        </Select>

                        <Select
                            style={{ width: 320 }}
                            placeholder="Seleccionar activitat de BBDD..."
                            allowClear
                            value={selectedItemKey}
                            onChange={(val) => setSelectedItemKey(val)}
                            size="large"
                            disabled={!selectedAula}
                        >
                            <OptGroup label="Activitats">
                                {allActivitats
                                    .filter((act: any) => String(act.id_aula) === String(selectedAula))
                                    .map((act: any) => (
                                        <Option key={`act-${act.id}`} value={`act-${act.id}`}>
                                            📘 {act.nom}
                                        </Option>
                                    ))}
                            </OptGroup>
                        </Select>

                        {selectedAula && !selectedItemKey && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Select
                                    value={viewMode}
                                    onChange={(val) => setViewMode(val)}
                                    size="large"
                                    style={{ width: 220 }}
                                    dropdownMatchSelectWidth={false}
                                >
                                    <Option value="aula">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <DragOutlined style={{ color: '#1890ff' }} /> Vista Aula (Lliure)
                                        </span>
                                    </Option>
                                    <Option value="graella">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <AppstoreOutlined style={{ color: '#722ed1' }} /> Vista Graella
                                        </span>
                                    </Option>
                                </Select>

                                {viewMode === 'aula' && (
                                    <Button
                                        icon={<UndoOutlined />}
                                        onClick={handleResetLayout}
                                        size="large"
                                        title="Restablir posicions inicials"
                                    />
                                )}
                            </div>
                        )}

                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchTeacherDashboard();
                                if (selectedAula) fetchClassroomStats(selectedAula);
                            }}
                            size="large"
                        >
                            Actualitzar
                        </Button>
                    </div>
                </div>

                {/* Subview 1: Activity Selected -> ActivitatAlumnesBbddModal */}
                {selectedItemKey && selectedItemKey.startsWith('act-') && selectedActivityObj && (
                    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                            <Button onClick={() => setSelectedItemKey(null)}>Tancar Activitat</Button>
                        </div>
                        <ActivitatAlumnesBbddModal
                            activitat={selectedActivityObj}
                            isPage={true}
                            visible={true}
                            onClose={() => setSelectedItemKey(null)}
                        />
                    </div>
                )}

                {/* Subview 2: Aula Only View (Stats + Free Classroom or Grid Layout) */}
                {!selectedItemKey && (
                    <>
                        {selectedAula && classroomData && (
                            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                                <Col xs={24} md={12} lg={14}>
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                                <Statistic
                                                    title="Total Alumnes"
                                                    value={processedStudents.length}
                                                    prefix={<UserOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col span={12}>
                                            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                                <Statistic
                                                    title="Activitats de BBDD"
                                                    value={aulaActivitatsCount}
                                                    valueStyle={{ color: '#1890ff' }}
                                                    prefix={<BookOutlined />}
                                                />
                                                <Text type="secondary" style={{ fontSize: 12 }}>Mitjana Aula: {globalStats.globalAvg.toFixed(2)} / 10</Text>
                                            </Card>
                                        </Col>

                                        <Col span={24}>
                                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 0 }} title="Mitjana per RA (0-10)">
                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                    {globalStats.raAverages.map((ra: any) => (
                                                        <div key={ra.nom} style={{ flex: 1, minWidth: 60, textAlign: 'center' }}>
                                                            <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>RA-{ra.nom}</div>
                                                            <div style={{ height: 60, background: '#f5f5f5', borderRadius: 4, position: 'relative', width: '100%' }}>
                                                                <Tooltip title={`${ra.avg.toFixed(1)}/10`}>
                                                                    <div style={{
                                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                        height: `${Math.min(ra.avg * 10, 100)}%`,
                                                                        background: getGradeColor(ra.avg),
                                                                        borderRadius: 4,
                                                                        transition: 'all 0.5s'
                                                                    }} />
                                                                </Tooltip>
                                                            </div>
                                                            <div style={{ fontSize: 11, fontWeight: 'bold', marginTop: 4 }}>{ra.avg.toFixed(1)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col xs={24} md={12} lg={10}>
                                    <Card
                                        title={<span><BarChartOutlined /> Distribució de Notes per RA</span>}
                                        bordered={false}
                                        style={{ borderRadius: 12, height: '100%', minHeight: 300 }}
                                        bodyStyle={{ padding: 20, height: 320 }}
                                    >
                                        <RaStackedBarChart
                                            students={processedStudents}
                                            ras={globalStats.raAverages.map((r: any) => r.nom)}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {loadingData ? (
                            <div style={{ textAlign: 'center', padding: 50 }}>
                                <Spin size="large" />
                                <p style={{ marginTop: 10, color: '#999' }}>Carregant dades de l'aula...</p>
                            </div>
                        ) : !selectedAula ? (
                            <Empty description="No hi ha cap aula amb activitats de Base de Dades disponible" style={{ marginTop: 100 }} />
                        ) : viewMode === 'aula' ? (
                            /* --- FREE CLASSROOM LAYOUT CANVAS --- */
                            <div style={{
                                background: '#ffffff',
                                border: '1px solid #e8e8e8',
                                borderRadius: 16,
                                padding: '24px 20px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                                marginBottom: 24
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom: '1px dashed #d9d9d9'
                                }}>
                                    <div>
                                        <Text strong style={{ fontSize: 16, color: '#262626' }}>🏫 Distribució de l'Aula (Taules)</Text>
                                        <Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>
                                            Arrossega les targetes lliurement per ubicar a cada alumne al seu lloc a l'aula
                                        </Text>
                                    </div>
                                    <Tag color="cyan" style={{ borderRadius: 12, padding: '2px 10px' }}>
                                        Arrossegar per moure • Es guarda automàticament
                                    </Tag>
                                </div>

                                <div
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const cardId = e.dataTransfer.getData("text/plain");
                                        if (!cardId) return;

                                        const offsetXRaw = e.dataTransfer.getData("application/x-offset-x");
                                        const offsetYRaw = e.dataTransfer.getData("application/x-offset-y");
                                        const offsetX = offsetXRaw ? parseFloat(offsetXRaw) : 0;
                                        const offsetY = offsetYRaw ? parseFloat(offsetYRaw) : 0;

                                        const container = e.currentTarget;
                                        const rect = container.getBoundingClientRect();

                                        const cardWidth = cardId === 'teacher' ? 440 : 235;
                                        const rawX = e.clientX - rect.left + container.scrollLeft - offsetX;
                                        const rawY = e.clientY - rect.top + container.scrollTop - offsetY;

                                        const maxX = Math.max(10, (container.scrollWidth || rect.width) - cardWidth - 10);
                                        const maxY = Math.max(10, (container.scrollHeight || 1200) - 150);

                                        const x = Math.max(10, Math.min(maxX, Math.round(rawX)));
                                        const y = Math.max(10, Math.min(maxY, Math.round(rawY)));

                                        const newPositions = { ...cardPositions, [cardId]: { x, y } };
                                        setCardPositions(newPositions);
                                        handleSaveLayout(newPositions);
                                    }}
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        minHeight: 900,
                                        background: '#fafbfc',
                                        border: '2px dashed #e6e9ed',
                                        borderRadius: 12,
                                        overflow: 'auto',
                                        backgroundImage: 'radial-gradient(#d3d9e0 1px, transparent 1px)',
                                        backgroundSize: '24px 24px'
                                    }}
                                >
                                    {/* TEACHER DESK CARD */}
                                    {(() => {
                                        const teacherPos = cardPositions['teacher'] || { x: 380, y: 20 };
                                        const teacherUser = user as any;
                                        const teacherAvatar = getAvatarSrc(teacherUser?.avatar);
                                        const teacherName = teacherUser?.nom ? `${teacherUser.nom} ${teacherUser.cognoms || ''}` : 'Profesor';

                                        return (
                                            <div
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", "teacher");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: teacherPos.x,
                                                    top: teacherPos.y,
                                                    width: 440,
                                                    cursor: 'grab',
                                                    zIndex: 20,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    size="small"
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 14,
                                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                        color: '#ffffff',
                                                        boxShadow: '0 6px 18px rgba(24, 144, 255, 0.35)',
                                                        border: '2px solid #69c0ff'
                                                    }}
                                                    bodyStyle={{ padding: '14px 18px' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                            <Avatar
                                                                size={54}
                                                                src={teacherAvatar}
                                                                style={{
                                                                    border: '2px solid #ffffff',
                                                                    backgroundColor: '#003a8c',
                                                                    color: '#fff',
                                                                    fontWeight: 'bold',
                                                                    fontSize: 20
                                                                }}
                                                            >
                                                                {teacherName[0]}
                                                            </Avatar>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <Tag color="#52c41a" style={{ color: '#fff', fontWeight: 'bold', border: 'none', borderRadius: 4 }}>
                                                                        TAULA PROFESSOR
                                                                    </Tag>
                                                                    <Tag color="#fff" style={{ color: '#096dd9', fontWeight: 600, border: 'none', borderRadius: 4 }}>
                                                                        DOCENT
                                                                    </Tag>
                                                                </div>
                                                                <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 4, color: '#ffffff' }}>
                                                                    {teacherName}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                                                                    {(user as any)?.email || 'Sessió docent activa'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                            <DragOutlined style={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                                                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Moure taula</span>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })()}

                                    {/* STUDENT DESK CARDS */}
                                    {processedStudents.map((student, index) => {
                                        const initials = ((student.nom?.[0] || '') + (student.cognoms?.[0] || '')).toUpperCase();
                                        const avatarColor = stringToColor((student.nom || '') + (student.cognoms || ''));

                                        const cols = 4;
                                        const colIndex = index % cols;
                                        const rowIndex = Math.floor(index / cols);
                                        const defaultX = 40 + colIndex * 260;
                                        const defaultY = 160 + rowIndex * 175;

                                        const pos = cardPositions[student.id + ""] || { x: defaultX, y: defaultY };

                                        return (
                                            <div
                                                key={student.id}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", student.id + "");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: pos.x,
                                                    top: pos.y,
                                                    width: 235,
                                                    cursor: 'grab',
                                                    zIndex: 10,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    hoverable
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 12,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                                        borderTop: '3px solid #1890ff',
                                                        background: '#ffffff'
                                                    }}
                                                    bodyStyle={{ padding: 12 }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                                        <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                            <Avatar
                                                                size={40}
                                                                src={student.avatar}
                                                                style={{
                                                                    backgroundColor: avatarColor,
                                                                    verticalAlign: 'middle',
                                                                    border: '1px solid #f0f0f0'
                                                                }}
                                                            >
                                                                {!student.avatar && initials}
                                                            </Avatar>
                                                        </div>

                                                        <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                            <Text
                                                                strong
                                                                onClick={() => handleStudentClick(student)}
                                                                style={{
                                                                    display: 'block',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: 13,
                                                                    lineHeight: 1.2,
                                                                    cursor: 'pointer',
                                                                    marginBottom: 4
                                                                }}
                                                                title={`${student.nom} ${student.cognoms}`}
                                                            >
                                                                {student.nom} {student.cognoms}
                                                            </Text>

                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                                    {student.completedTasks}/{student.totalTasks} act.
                                                                </Text>

                                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                    <Tooltip title="Impersonar Alumne">
                                                                        <UserSwitchOutlined
                                                                            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                                                                            onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id + ""); }}
                                                                        />
                                                                    </Tooltip>
                                                                    <DragOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Numeric RA Grade Indicators */}
                                                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 6 }}>
                                                        {student.raGrades && student.raGrades.length > 0 ? student.raGrades.map((ra: any, idx: number) => {
                                                            const val = ra.nota !== null ? ra.nota : 0;
                                                            const color = ra.nota !== null ? getGradeColor(val) : '#f0f0f0';

                                                            return (
                                                                <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                    <div
                                                                        onClick={() => handleStudentClick(student)}
                                                                        style={{
                                                                            width: 22,
                                                                            height: 14,
                                                                            borderRadius: 2,
                                                                            backgroundColor: color,
                                                                            border: '1px solid #e0e0e0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '8px',
                                                                            fontWeight: 'bold',
                                                                            color: ra.nota !== null && ra.nota >= 5 ? '#fff' : '#595959',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        {ra.nota !== null ? ra.nota.toFixed(1) : '-'}
                                                                    </div>
                                                                </Tooltip>
                                                            );
                                                        }) : (
                                                            <Text type="secondary" style={{ fontSize: 10, width: '100%', textAlign: 'center' }}>Sense dades</Text>
                                                        )}
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* --- STANDARD GRID LAYOUT (VISTA GRAELLA) --- */
                            <Row gutter={[16, 16]}>
                                {processedStudents.map(student => {
                                    const initials = ((student.nom?.[0] || '') + (student.cognoms?.[0] || '')).toUpperCase();
                                    const avatarColor = stringToColor((student.nom || '') + (student.cognoms || ''));

                                    return (
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={student.id}>
                                            <Card
                                                hoverable
                                                bordered={false}
                                                style={{
                                                    borderRadius: 12,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                    borderTop: '3px solid #1890ff',
                                                    transition: 'transform 0.2s',
                                                    cursor: 'default'
                                                }}
                                                bodyStyle={{ padding: 12 }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                                    <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                        <Avatar
                                                            size={42}
                                                            src={student.avatar}
                                                            style={{
                                                                backgroundColor: avatarColor,
                                                                verticalAlign: 'middle',
                                                                border: '1px solid #f0f0f0'
                                                            }}
                                                        >
                                                            {!student.avatar && initials}
                                                        </Avatar>
                                                    </div>

                                                    <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                        <Text
                                                            strong
                                                            onClick={() => handleStudentClick(student)}
                                                            style={{
                                                                display: 'block',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                fontSize: 14,
                                                                lineHeight: 1.2,
                                                                cursor: 'pointer',
                                                                marginBottom: 4
                                                            }}
                                                            title={`${student.nom} ${student.cognoms}`}
                                                        >
                                                            {student.nom} {student.cognoms}
                                                        </Text>

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                                {student.completedTasks}/{student.totalTasks} act.
                                                            </Text>

                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                <Tooltip title="Impersonar Alumne">
                                                                    <UserSwitchOutlined
                                                                        style={{ color: '#1890ff', cursor: 'pointer', fontSize: 14 }}
                                                                        onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id + ""); }}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Numeric RA Grade Indicators */}
                                                <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 10 }}>
                                                    {student.raGrades && student.raGrades.length > 0 ? student.raGrades.map((ra: any, idx: number) => {
                                                        const val = ra.nota !== null ? ra.nota : 0;
                                                        const color = ra.nota !== null ? getGradeColor(val) : '#f0f0f0';

                                                        return (
                                                            <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                <div
                                                                    onClick={() => handleStudentClick(student)}
                                                                    style={{
                                                                        width: 24,
                                                                        height: 14,
                                                                        borderRadius: 2,
                                                                        backgroundColor: color,
                                                                        border: '1px solid #e0e0e0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '8px',
                                                                        fontWeight: 'bold',
                                                                        color: ra.nota !== null && ra.nota >= 5 ? '#fff' : '#595959',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {ra.nota !== null ? ra.nota.toFixed(1) : '-'}
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    }) : (
                                                        <Text type="secondary" style={{ fontSize: 10, width: '100%', textAlign: 'center' }}>Sense dades</Text>
                                                    )}
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}

                        <StudentDetailModal
                            visible={modalVisible}
                            student={selectedStudent}
                            classroomData={classroomData}
                            onClose={() => setModalVisible(false)}
                            raConfig={raConfig}
                        />
                    </>
                )}
            </Content>
        </>
    );
};

export default Dashboard;
